import { AlertTriangle, RefreshCw, WifiOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ErrorRecoveryProps {
  error: string | null;
  isRetrying: boolean;
  retryCount: number;
  isOffline: boolean;
  onRetry: () => void;
  className?: string;
  compact?: boolean;
}

/**
 * Wiederverwendbare Error-Recovery UI Komponente
 * Zeigt Offline-Status, Retry-Progress und manuelle Retry-Option
 */
export function ErrorRecovery({
  error,
  isRetrying,
  retryCount,
  isOffline,
  onRetry,
  className,
  compact = false,
}: ErrorRecoveryProps) {
  if (!error && !isRetrying && !isOffline) return null;

  // Offline State
  if (isOffline) {
    return (
      <div
        className={cn(
          "flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4",
          compact && "p-3",
          className
        )}
      >
        <WifiOff className={cn("h-5 w-5 text-amber-500 shrink-0", compact && "h-4 w-4")} />
        <div className="flex-1 min-w-0">
          <p className={cn("text-sm font-medium text-amber-700 dark:text-amber-400", compact && "text-xs")}>
            Du bist offline
          </p>
          <p className={cn("text-xs text-amber-600/80 dark:text-amber-400/70 mt-0.5", compact && "hidden")}>
            Verbindung wird automatisch wiederhergestellt...
          </p>
        </div>
        <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
      </div>
    );
  }

  // Retrying State
  if (isRetrying) {
    return (
      <div
        className={cn(
          "flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4",
          compact && "p-3",
          className
        )}
      >
        <Loader2 className={cn("h-5 w-5 text-primary animate-spin shrink-0", compact && "h-4 w-4")} />
        <div className="flex-1 min-w-0">
          <p className={cn("text-sm font-medium text-foreground", compact && "text-xs")}>
            Verbindung wird wiederhergestellt...
          </p>
          <p className={cn("text-xs text-muted-foreground mt-0.5", compact && "hidden")}>
            Automatischer Retry {retryCount}/3
          </p>
        </div>
        <div className="flex gap-1">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className={cn(
                "h-1.5 w-1.5 rounded-full transition-colors",
                i <= retryCount ? "bg-primary" : "bg-border"
              )}
            />
          ))}
        </div>
      </div>
    );
  }

  // Error State with Retry Option
  if (error) {
    return (
      <div
        className={cn(
          "flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4",
          compact && "p-3 items-center",
          className
        )}
      >
        <AlertTriangle className={cn("h-5 w-5 text-destructive shrink-0 mt-0.5", compact && "h-4 w-4 mt-0")} />
        <div className="flex-1 min-w-0">
          <p className={cn("text-sm font-medium text-destructive", compact && "text-xs")}>
            Verbindungsfehler
          </p>
          <p className={cn("text-xs text-muted-foreground mt-1 line-clamp-2", compact && "hidden")}>
            {error}
          </p>
        </div>
        <Button
          variant="outline"
          size={compact ? "sm" : "default"}
          onClick={onRetry}
          className={cn(
            "shrink-0 gap-2 border-destructive/30 hover:bg-destructive/10",
            compact && "h-8 px-3 text-xs"
          )}
        >
          <RefreshCw className={cn("h-4 w-4", compact && "h-3 w-3")} />
          <span className={compact ? "hidden sm:inline" : ""}>Erneut versuchen</span>
        </Button>
      </div>
    );
  }

  return null;
}

/**
 * Inline Retry Button für kompakte UIs
 */
export function InlineRetryButton({
  onRetry,
  isRetrying,
  className,
}: {
  onRetry: () => void;
  isRetrying: boolean;
  className?: string;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onRetry}
      disabled={isRetrying}
      className={cn("gap-2 text-destructive hover:text-destructive hover:bg-destructive/10", className)}
    >
      {isRetrying ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <RefreshCw className="h-3.5 w-3.5" />
      )}
      {isRetrying ? "Verbinde..." : "Erneut versuchen"}
    </Button>
  );
}
